import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosRequestConfig } from 'axios';

import {
  postLogin,
  postLogout,
  postNodeLogin,
  postNodeLogout,
  postRefreshAuthToken,
} from '../api/auth';
import {
  postGetFollowingItemList,
  postGetUserChatPrompt,
  postGetUserProfile,
  postGetUserSubscribing,
  postUpdateUserChatPrompt,
} from '../api/user';
import { storage } from '../storage';
import {
  EFollowType,
  ELoginType,
  ESubscribeType,
  IAuthData,
  IChatData,
  IIMAccountInfo,
  IRegisterDeviceReq,
  ISubscribingUserBaseInfo,
  IUpdateUserChatPromptReq,
  IUserChatPrompt,
  OSType,
} from '../types';
import { isTalkie } from '../utils/env';
import { underscoreToCamelCase } from '../utils';
import { RootState } from './rootReducers';

export const getUserInfo = createAsyncThunk('user/getUserInfo', async (_, { getState }) => {
  const state = getState() as RootState;
  return await postGetUserProfile({ user_id: state.user.authData.userId });
});

export const login = createAsyncThunk(
  'user/login',
  async (
    {
      idToken,
      deviceInfo,
      notLogoutOtherDevice = false,
    }: { idToken: string; deviceInfo: IRegisterDeviceReq; notLogoutOtherDevice?: boolean },
    { rejectWithValue }
  ) => {
    try {
      return await postNodeLogin({
        login_type: ELoginType.FIREBASE,
        firebase_token: idToken,
        device_info: deviceInfo,
        not_logout_other_device: notLogoutOtherDevice,
      });
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const logout = createAsyncThunk('user/logout', async (_, { getState }) => {
  const state = getState() as RootState;
  try {
    // 当被顶号的时候这个接口会失败，但不能阻塞后续退登清除 cookie
    await postLogout({ refresh_token: state.user.authData.refreshToken || '' });
  } catch { }
  return await postNodeLogout();
});

export const anonymousLogin = createAsyncThunk(
  'user/anonymousLogin',
  async ({ notRefresh }: { notRefresh?: boolean }, { rejectWithValue, getState }) => {
    try {
      const {
        user: {
          authData: { uuid },
        },
      } = getState() as RootState;
      const res = await postNodeLogin({
        login_type: ELoginType.ANONYMOUS,
        device_info: { uuid, os: OSType.WEB },
      });
      return {
        ...res,
        notRefresh,
      };
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const refreshAuthToken = createAsyncThunk(
  'user/refreshAuthToken',
  async (_, { getState }) => {
    const state = getState() as RootState;
    return await postRefreshAuthToken({
      refresh_token: state.user.authData.refreshToken || '',
    });
  }
);

export const getFollowingListData = createAsyncThunk(
  'user/getFollowingListData',
  async (_, { getState }) => {
    const state = getState() as RootState;
    const userId = state.user.authData.userId;
    const page = state.user.following.page;
    return await postGetFollowingItemList({ user_id: userId, page, size: 20 });
  }
);

export const getSubscribingListData = createAsyncThunk(
  'user/getSubscribingListData',
  async (_, { getState }) => {
    const state = getState() as RootState;
    const userId = state.user.authData.userId;
    const page = state.user.subscribing.page;
    const hasMore = state.user.subscribing.hasMore;
    if (hasMore) {
      return await postGetUserSubscribing({ user_id: userId, page, size: 20 });
    }
  }
);

export const getUserInfoWithUserId = createAsyncThunk(
  'user/getUserInfoWithUserId',
  async ({ userId, config = {} }: { userId: number; config?: AxiosRequestConfig }) => {
    return await postGetUserProfile({ user_id: userId }, config);
  }
);

export const getUserChatPrompt = createAsyncThunk('user/getUserChatPrompt', async () => {
  return await postGetUserChatPrompt({});
});

export const updateUserChatPrompt = createAsyncThunk(
  'user/updateUserChatPrompt',
  async (data: IUpdateUserChatPromptReq) => {
    await postUpdateUserChatPrompt(data);
    return data;
  }
);

interface IUserProfile {
  uniqueId: number;
  userId: number;
  nickname: string;
  avatarUrl: string;
  statisticInfo: {
    followingCount: number;
    linkingCount: number;
  };
}

export interface ISignInPopData {
  open: boolean;
  source: string;
  title?: string;
  subTitle?: string;
  popupType?: string; // 埋点使用
  isMandatoryLogin?: boolean; // 埋点使用，是否强制登录
  backgroundImgUrl?: string;
}

interface IAgeGatePopData {
  open: boolean;
  providerUid: string;
  age: string; // 年龄（生日），格式：year-month-day
  ageResult: boolean; // 年龄设置结果，false：没过年龄检测
}

interface IFollowingData {
  hasMore: boolean;
  list: IChatData[];
  page: number;
  total: number;
}

interface ISubscribingData {
  hasMore: boolean;
  list: ISubscribingUserBaseInfo[];
  page: number;
  total: number;
}

export interface IUserState {
  authData: IAuthData;
  userProfile: IUserProfile;
  signInPopData: ISignInPopData;
  ageGatePopData: IAgeGatePopData;
  following: IFollowingData;
  subscribing: ISubscribingData;
  userChatPrompt: IUserChatPrompt; // 全局对话人设
}

const initialImAcountInfo: IIMAccountInfo = {
  account: '',
  password: '',
};

const initialAuthData: IAuthData = {
  userId: 0,
  authToken: '',
  isAnonymous: true,
  referralCode: '',
  deviceId: 0,
  uuid: '',
  imAccountInfo: {
    ...initialImAcountInfo,
  },
};

const initialUserProfile: IUserProfile = {
  uniqueId: 0,
  userId: 0,
  nickname: 'nickname',
  avatarUrl: '',
  statisticInfo: {
    followingCount: 0,
    linkingCount: 0,
  },
};

const initialAgeGatePopData: IAgeGatePopData = {
  open: false,
  providerUid: '',
  age: '',
  ageResult: false,
};

const initialState: IUserState = {
  authData: {
    ...initialAuthData,
  },
  userProfile: {
    ...initialUserProfile,
  },
  signInPopData: {
    open: false,
    title: '',
    subTitle: '',
    source: '',
    popupType: 'user_initiated',
    backgroundImgUrl: '',
    isMandatoryLogin: false,
  },
  ageGatePopData: {
    ...initialAgeGatePopData,
  },
  following: {
    total: 0,
    hasMore: true,
    list: [],
    page: 0,
  },
  subscribing: {
    total: 0,
    hasMore: true,
    list: [],
    page: 0,
  },
  userChatPrompt: {},
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    initAuthData: (state, action: PayloadAction<IAuthData>) => {
      state.authData = {
        imAccountInfo: state.authData.imAccountInfo || { ...initialImAcountInfo },
        ...action.payload,
      };
    },
    initImData: (
      state,
      action: PayloadAction<{ imAccountInfo?: IAuthData['imAccountInfo']; imToken?: string }>
    ) => {
      if (action.payload.imAccountInfo?.account && action.payload.imAccountInfo?.password) {
        state.authData.imAccountInfo = { ...action.payload.imAccountInfo };
      }
      if (action.payload.imToken) {
        state.authData.imToken = action.payload.imToken;
      }
    },
    initImToken(state, action: PayloadAction<string>) {
      state.authData.imToken = action.payload;
      storage.easeMob.setToken(action.payload);
    },
    clearImToken(state) {
      state.authData.imToken = undefined;
      storage.easeMob.clearToken();
    },
    openSignInPop: (
      state,
      action: PayloadAction<{
        title?: string;
        subTitle?: string;
        popupType?: string;
        backgroundImgUrl?: string;
        isMandatoryLogin?: boolean;
      }>
    ) => {
      state.signInPopData.open = true;
      state.signInPopData.title = action.payload.title;
      state.signInPopData.subTitle = action.payload.subTitle;
      state.signInPopData.backgroundImgUrl = action.payload.backgroundImgUrl;
      state.signInPopData.isMandatoryLogin = action.payload.isMandatoryLogin;
      if (action.payload.popupType) {
        state.signInPopData.popupType = action.payload.popupType;
      }
    },
    closeSignInPop: (state) => {
      state.signInPopData.open = false;
      state.signInPopData.title = '';
      state.signInPopData.popupType = 'user_initiated';
      state.signInPopData.subTitle = '';
      state.signInPopData.backgroundImgUrl = '';
      state.signInPopData.isMandatoryLogin = false;
    },
    openAgeGatePop: (state, action: PayloadAction<{ providerUid?: string }>) => {
      state.ageGatePopData = {
        ...state.ageGatePopData,
        open: true,
        providerUid: action.payload.providerUid || state.ageGatePopData.providerUid,
      };
      state.signInPopData.open = false;
    },
    closeAgeGatePop: (state) => {
      state.ageGatePopData = {
        ...state.ageGatePopData,
        open: false,
      };
    },
    setAge: (
      state,
      action: PayloadAction<{ age: string; ageResult: boolean; providerUid?: string }>
    ) => {
      state.ageGatePopData = {
        ...state.ageGatePopData,
        ...action.payload,
      };
    },
    setSubscribeStatus: (
      state,
      action: PayloadAction<{ user_id: number; status: ESubscribeType }>
    ) => {
      const idx = state.subscribing.list.findIndex(
        (item) => item.user_id === action.payload.user_id
      );
      if (idx !== -1) state.subscribing.list.splice(idx, 1);
    },
    setFollowStatus: (state, action: PayloadAction<{ npcId: number; status: EFollowType }>) => {
      const idx = state.following.list.findIndex(
        (item) => item.npc_info?.npc_id === action.payload.npcId
      );
      if (idx !== -1) state.following.list.splice(idx, 1);
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getUserInfo.fulfilled, (state, action) => {
        const { user_id } = action.payload;
        state.authData.userId = user_id;
        const userProfile = underscoreToCamelCase(action.payload);
        delete userProfile.user_id;
        state.userProfile = {
          ...userProfile,
        };
      })
      .addCase(getUserInfo.rejected, (_, action) => {
        console.error('get user info error', action.error);
      });
    builder
      .addCase(login.fulfilled, (state, action) => {
        state.signInPopData.open = false;
        storage.auth.setRefreshTokenTime();
        // user/token 相关数据写 storage,apps/agent (CSR SPA) 从 storage 取鉴权信息。
        // 海外 talkie 之前漏写,导致 PC web 用户访问 /agent/npc-lab 时 apps/agent
        // getUserInfoFromLocalStorage() 返 null → fetch 拼 ?user_id=0 → weaver auth 401。
        // 跟 apps/xingye/pages/create/login/index.tsx:170 对齐 (xingye 那条调用保留, 幂等)。
        const { im_account_info, ...rest } = action.payload || {};
        const camelCaseData = underscoreToCamelCase(rest);
        storage.auth.saveAuthData({ ...camelCaseData }, 'login thunk');
        if (action.payload.im_account_info?.account && action.payload.im_account_info?.password) {
          storage.auth.saveAuthData(
            {
              imAccountInfo: action.payload.im_account_info as IIMAccountInfo,
            },
            'login thunk'
          );
          storage.easeMob.clearToken();
        }
      })
      .addCase(login.rejected, (_, action) => {
        console.error('login error', action.error);
      });
    builder
      .addCase(logout.fulfilled, (state, action) => {
        storage.auth.setRefreshTokenTime();
        storage.auth.clearLocalAuthData();
        storage.easeMob.clearToken();
      })
      .addCase(logout.rejected, (_, action) => {
        console.error('logout error', action.error);
      });
    builder.addCase(refreshAuthToken.fulfilled, () => {
      storage.auth.setRefreshTokenTime();
    });
    builder
      .addCase(getUserInfoWithUserId.fulfilled, (state, action) => {
        const { user_id } = action.payload;
        state.authData.userId = user_id;
        const userProfile = underscoreToCamelCase(action.payload);
        delete userProfile.user_id;
        state.userProfile = {
          ...userProfile,
        };
      })
      .addCase(getUserInfoWithUserId.rejected, (_, action) => {
        console.error('get user info with userId error', action.error);
      });
    builder
      .addCase(getFollowingListData.fulfilled, (state, action) => {
        const { item_list = [], has_more } = action.payload || {};
        if (has_more !== undefined && item_list !== undefined) {
          state.following.hasMore = has_more;
          state.following.list = [...state.following.list, ...item_list];
          state.following.page += 1;
          // 接口需要添加 total
          state.following.total = 0;
        }
      })
      .addCase(getFollowingListData.rejected, (_, action) => {
        console.error('get following list data error', action.error);
      });
    builder
      .addCase(getSubscribingListData.fulfilled, (state, action) => {
        const { user_list = [], has_more } = action.payload || {};
        if (has_more !== undefined && user_list !== undefined) {
          state.subscribing.hasMore = has_more;
          state.subscribing.list = [...state.subscribing.list, ...user_list];
          state.subscribing.page += 1;
          state.subscribing.total = 0;
        }
      })
      .addCase(getSubscribingListData.rejected, (_, action) => {
        console.error('get subscribing list data error', action.error);
      });
    builder
      .addCase(getUserChatPrompt.fulfilled, (state, action) => {
        const { user_chat_prompt } = action.payload || {};
        if (user_chat_prompt) {
          state.userChatPrompt = user_chat_prompt;
        }
      })
      .addCase(getUserChatPrompt.rejected, (_, action) => {
        console.error('get user chat prompt data error', action.error);
      });
    builder
      .addCase(updateUserChatPrompt.fulfilled, (state, action) => {
        const { user_chat_prompt } = action.payload || {};
        if (user_chat_prompt) {
          state.userChatPrompt = user_chat_prompt;
        }
      })
      .addCase(updateUserChatPrompt.rejected, (_, action) => {
        console.error('update user chat prompt data error', action.error);
      });
  },
});

const { actions, reducer } = userSlice;
export const {
  initAuthData,
  openSignInPop,
  closeSignInPop,
  openAgeGatePop,
  closeAgeGatePop,
  setAge,
  initImData,
  initImToken,
  clearImToken,
  setFollowStatus,
  setSubscribeStatus,
} = actions;
export default reducer;
